import { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowUpRight, Send } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fadeUp = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-60px' },
};

export default function Contact() {
  const [form, setForm] = useState({ name: '', email: '', message: '', website: '' });
  const [status, setStatus] = useState('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) return;
    setStatus('sending');
    setErrorMsg('');
    try {
      await axios.post(`${API}/contact`, form);
      setStatus('sent');
      setForm({ name: '', email: '', message: '', website: '' });
    } catch (err) {
      setStatus('error');
      const detail = err.response?.data?.detail;
      if (Array.isArray(detail)) {
        const emailErr = detail.find((d) => d.loc?.includes('email'));
        setErrorMsg(emailErr?.msg || 'Something went wrong. Try again.');
      } else {
        setErrorMsg(typeof detail === 'string' ? detail : 'Something went wrong. Try again.');
      }
    }
  };

  return (
    <section id="contact" className="py-24 md:py-36" data-testid="contact-section">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="section-divider mb-12" />

        <motion.p className="section-number" {...fadeUp} data-testid="contact-number">
          06 / Begin
        </motion.p>

        {/* Why this matters + CTA */}
        <motion.p
          className="text-[var(--cv-text-dim)] text-sm md:text-base max-w-[560px] mb-10"
          style={{ fontFamily: 'var(--font-body)', lineHeight: 1.8 }}
          {...fadeUp}
          transition={{ duration: 0.8, delay: 0.05, ease: [0.16, 1, 0.3, 1] }}
          data-testid="contact-why"
        >
          Most ideas stay vague because no one helps give them form.
          Bring yours here. Whether it&apos;s a half-formed concept,
          a business question, or something you just want to
          explore &mdash; we&apos;ll help you find the next step.
        </motion.p>

        <motion.div
          {...fadeUp}
          transition={{ duration: 0.8, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
          className="mb-16 md:mb-24"
        >
          <a href="mailto:hello@civicventures.io" className="footer-cta" data-testid="contact-cta">
            Let&apos;s
            <br />
            Start
            <ArrowUpRight
              className="inline-block ml-2 md:ml-4"
              size={typeof window !== 'undefined' && window.innerWidth < 768 ? 28 : 56}
              strokeWidth={1.5}
            />
          </a>
        </motion.div>

        {/* Contact form */}
        <motion.form
          onSubmit={handleSubmit}
          className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-24"
          {...fadeUp}
          transition={{ duration: 0.8, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
          data-testid="contact-form"
        >
          <div className="flex flex-col gap-2">
            <label
              className="text-xs uppercase tracking-[0.15em] text-[var(--cv-text-dim)]"
              style={{ fontFamily: 'var(--font-mono)' }}
              htmlFor="contact-name"
            >
              Name
            </label>
            <input
              id="contact-name"
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="bg-transparent border-b border-[var(--cv-border)] py-3 text-white outline-none focus:border-white transition-colors"
              style={{ fontFamily: 'var(--font-body)' }}
              placeholder="Your name"
              data-testid="contact-name-input"
            />
          </div>
          <div className="flex flex-col gap-2">
            <label
              className="text-xs uppercase tracking-[0.15em] text-[var(--cv-text-dim)]"
              style={{ fontFamily: 'var(--font-mono)' }}
              htmlFor="contact-email"
            >
              Email
            </label>
            <input
              id="contact-email"
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="bg-transparent border-b border-[var(--cv-border)] py-3 text-white outline-none focus:border-white transition-colors"
              style={{ fontFamily: 'var(--font-body)' }}
              placeholder="you@example.com"
              data-testid="contact-email-input"
            />
          </div>
          <div className="flex flex-col gap-2 md:col-span-2">
            <label
              className="text-xs uppercase tracking-[0.15em] text-[var(--cv-text-dim)]"
              style={{ fontFamily: 'var(--font-mono)' }}
              htmlFor="contact-message"
            >
              What are you working on?
            </label>
            <textarea
              id="contact-message"
              rows={4}
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              className="bg-transparent border-b border-[var(--cv-border)] py-3 text-white outline-none focus:border-white transition-colors resize-none"
              style={{ fontFamily: 'var(--font-body)' }}
              placeholder="Tell us about your idea, challenge, or ambition..."
              data-testid="contact-message-input"
            />
          </div>
          {/* Honeypot — hidden from humans, bots fill this */}
          <div className="absolute opacity-0 h-0 overflow-hidden" aria-hidden="true" tabIndex={-1}>
            <input
              type="text"
              name="website"
              value={form.website}
              onChange={(e) => setForm({ ...form, website: e.target.value })}
              autoComplete="off"
              tabIndex={-1}
            />
          </div>
          <div className="md:col-span-2 flex items-center gap-4">
            <button
              type="submit"
              disabled={status === 'sending'}
              className="flex items-center gap-3 bg-white text-[var(--cv-bg)] px-8 py-3 text-sm uppercase tracking-[0.1em] font-semibold hover:bg-[var(--cv-text-dim)] transition-colors disabled:opacity-50"
              style={{ fontFamily: 'var(--font-body)' }}
              data-testid="contact-submit-btn"
            >
              {status === 'sending' ? 'Sending...' : 'Send'}
              <Send size={14} />
            </button>
            {status === 'sent' && (
              <span className="text-sm text-[var(--cv-text-dim)]" style={{ fontFamily: 'var(--font-mono)' }} data-testid="contact-success">
                Message received. We&apos;ll be in touch.
              </span>
            )}
            {status === 'error' && (
              <span className="text-sm text-red-400" style={{ fontFamily: 'var(--font-mono)' }} data-testid="contact-error">
                {errorMsg}
              </span>
            )}
          </div>
        </motion.form>

        {/* Footer bottom */}
        <div className="section-divider mb-8" />
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6" data-testid="footer-bottom">
          <div>
            <p
              className="text-xs uppercase tracking-[0.2em] text-[var(--cv-text-dim)]"
              style={{ fontFamily: 'var(--font-mono)' }}
              data-testid="footer-copyright"
            >
              &copy; {new Date().getFullYear()} Civic Ventures LLC. All rights reserved.
            </p>
          </div>
          <p
            className="text-xs uppercase tracking-[0.15em] text-[var(--cv-text-dim)]"
            style={{ fontFamily: 'var(--font-mono)' }}
            data-testid="footer-location"
          >
            Creative AI Venture Studio
          </p>
          <a
            href="mailto:hello@civicventures.io"
            className="contact-email text-sm"
            data-testid="footer-email"
          >
            hello@civicventures.io
          </a>
        </div>
      </div>
    </section>
  );
}
